import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, ApiError, formatDate, type Progress, type Segment } from '../api.ts';
import { Container } from '../components/Layout.tsx';
import { Badge as Medal, Confetti, Toast } from '../components/Progress.tsx';
import { Segments } from '../components/Segments.tsx';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckIcon,
  cx,
  ErrorMessage,
  KindIcon,
  kindLabel,
  LinkButton,
  Loading,
  TextArea,
} from '../components/ui.tsx';
import { useAction, useApi, useAutosave, useTitle } from '../hooks.ts';
import { isTeacher, useAuth } from '../auth.tsx';

interface Submission {
  id: number;
  attempt: number;
  text: string;
  file_name: string | null;
  file_size: number | null;
  status: 'submitted' | 'approved' | 'revise';
  feedback: string | null;
  submitted_at: string;
  graded_at: string | null;
  rubric: Record<string, boolean> | null;
}

interface ItemResponse {
  item: {
    id: number;
    kind: string;
    title: string;
    required: boolean;
    estimatedMinutes: number | null;
    content: Segment[];
    completed: boolean;
    selfCompletable: boolean;
    quiz?: {
      attemptToken: string;
      passPercent: number;
      assessment: 'pre' | 'post' | null;
      questions: {
        id: number;
        kind: 'single' | 'multiple' | 'truefalse' | 'matching' | 'ordering' | 'short';
        text: Segment[];
        choices: { id: string; text: string }[];
        /** Matchning: högertexterna, blandade. */
        matches?: { id: string; text: string }[];
      }[];
      /** Antal frågor i frågebanken, när ett urval visas. */
      bankSize: number | null;
      attempts: { id: number; score: number; max_score: number; passed: boolean; created_at: string }[];
    };
    submissions?: Submission[];
    reflection?: { minWords: number };
    rubric?: { id: string; text: string }[];
    discussion?: { minWords: number };
    meeting?: { startsAt: string | null; location: string | null; url: string | null };
    attendanceRequired?: boolean;
  };
  module: {
    id: number;
    title: string;
    number: number;
    count: number;
    items: { id: number; title: string; kind: string; required: boolean; completed: boolean }[];
  };
  prev: { id: number; title: string } | null;
  next: { id: number; title: string; moduleChange: boolean } | null;
  progress: Progress;
}

interface Celebration {
  kind: 'module' | 'course';
  moduleNumber: number;
  moduleTitle: string;
}

export function ItemPage() {
  const { id } = useParams();
  const { data, error, loading, reload, setData } = useApi<ItemResponse>(`/api/course/items/${id}`);
  const teacher = isTeacher(useAuth().user);
  const [toast, setToast] = useState<string | null>(null);
  const [confetti, setConfetti] = useState(0);
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const previous = useRef<{
    itemId: number;
    completed: boolean;
    moduleDone: boolean;
    allDone: boolean;
  } | null>(null);
  useTitle(data?.item.title ?? 'Kursen');

  // Fira när delen, modulen eller hela kursen blir klar medan deltagaren är på sidan.
  useEffect(() => {
    if (!data) return;
    const required = data.module.items.filter((i) => i.required);
    const moduleDone = required.length > 0 && required.every((i) => i.completed);
    const allDone = data.progress.required > 0 && data.progress.completed >= data.progress.required;
    const prev = previous.current;
    previous.current = { itemId: data.item.id, completed: data.item.completed, moduleDone, allDone };
    if (!prev || prev.itemId !== data.item.id) return;
    if (!prev.allDone && allDone) {
      setCelebration({ kind: 'course', moduleNumber: data.module.number, moduleTitle: data.module.title });
      setConfetti((n) => n + 1);
    } else if (!prev.moduleDone && moduleDone) {
      setCelebration({ kind: 'module', moduleNumber: data.module.number, moduleTitle: data.module.title });
      setConfetti((n) => n + 1);
    } else if (!prev.completed && data.item.completed) {
      setToast(PRAISE[Math.floor(Math.random() * PRAISE.length)]);
    }
  }, [data]);

  const markDone = useCallback(async () => {
    // Uppgifter, quiz och diskussioner blir klara på annat sätt (t.ex. en film i en uppgift).
    if (!data || data.item.completed || !data.item.selfCompletable) return;
    const completedId = data.item.id;
    try {
      await api.post(`/api/course/items/${data.item.id}/complete`);
    } catch {
      return;
    }
    setData((d) =>
      d && d.item.id === completedId
        ? {
            ...d,
            item: { ...d.item, completed: true },
            module: {
              ...d.module,
              items: d.module.items.map((i) => (i.id === d.item.id ? { ...i, completed: true } : i)),
            },
            progress: d.item.completed
              ? d.progress
              : { ...d.progress, completed: d.progress.completed + (d.item.required ? 1 : 0) },
          }
        : d,
    );
  }, [data, setData]);

  if (loading && !data) return <Loading />;
  if (error || !data) {
    return (
      <Container narrow>
        <Alert>{error?.message ?? 'Delen kunde inte hämtas.'}</Alert>
        <p className="mt-4">
          <Link to="/kurs" className="text-night-700 underline">
            Tillbaka till kursen
          </Link>
        </p>
      </Container>
    );
  }
  const { item } = data;
  const percent = data.progress.required
    ? Math.round((data.progress.completed / data.progress.required) * 100)
    : 0;
  const moduleRequired = data.module.items.filter((i) => i.required);
  const moduleDoneCount = moduleRequired.filter((i) => i.completed).length;
  const modulePercent = moduleRequired.length
    ? Math.round((moduleDoneCount / moduleRequired.length) * 100)
    : 0;

  return (
    <>
      <div className="sticky top-0 z-30 border-b border-night-800/10 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-2 text-sm">
          <Link to="/kurs" className="hidden shrink-0 font-semibold text-night-700 hover:underline sm:block">
            ← Kursen
          </Link>
          <span className="hidden shrink-0 text-muted md:block">
            Modul {data.module.number} av {data.module.count}
          </span>
          <div
            className="h-2 flex-1 overflow-hidden rounded-full bg-night-100"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Hela kursen"
          >
            <div
              className="shimmer h-full rounded-full bg-[linear-gradient(90deg,#2e8b6e,#3fb68b,#a48cf0,#3fb68b,#2e8b6e)] transition-all duration-700"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="shrink-0 font-bold tabular-nums text-night-900">{percent} %</span>
        </div>
      </div>

      <Container>
        <nav aria-label="Brödsmulor" className="mb-4 text-sm text-muted">
          <Link to="/kurs" className="underline">
            Kursen
          </Link>{' '}
          /{' '}
          <Link to={`/kurs/modul/${data.module.id}`} className="underline">
            {data.module.title}
          </Link>
          {teacher && (
            <span className="float-right flex gap-3">
              <Link to={`/larare/innehall#modul-${data.module.id}`} className="font-semibold underline">
                ✏️ Redigera modulen
              </Link>
              <Link to={`/larare/innehall/del/${item.id}`} className="font-semibold underline">
                ✏️ Redigera delen
              </Link>
            </span>
          )}
        </nav>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <article className="fade-up min-w-0" key={item.id}>
            <Card>
              <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-night-700">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-night-100">
                      <KindIcon kind={item.kind} className="h-4 w-4" />
                    </span>
                    {kindLabel(item.kind)}
                    {item.estimatedMinutes ? (
                      <span className="font-normal text-muted">· cirka {item.estimatedMinutes} min</span>
                    ) : null}
                    {!item.required && <Badge tone="blue">Frivillig</Badge>}
                  </p>
                  <h1 className="mt-3 text-2xl font-extrabold text-night-900 sm:text-3xl">{item.title}</h1>
                </div>
                {item.completed && (
                  <span className="pop-in flex items-center gap-1.5 rounded-full bg-gradient-to-r from-aurora-500 to-aurora-400 px-3 py-1 text-sm font-semibold text-white shadow">
                    <CheckIcon className="h-4 w-4 text-white/30" /> Klar
                  </span>
                )}
              </header>

              {item.meeting && (
                <MeetingInfo
                  meeting={item.meeting}
                  calendarUrl={item.kind === 'meeting' ? `/api/course/items/${item.id}/calendar.ics` : null}
                />
              )}
              <Segments segments={item.content} onWatched={markDone} onAudioEnded={markDone} />

              {item.kind === 'quiz' && item.quiz && (
                <Quiz itemId={item.id} quiz={item.quiz} onChanged={reload} />
              )}
              {(item.kind === 'discussion' || item.kind === 'tryout') && item.discussion && (
                <Discussion
                  tryout={item.kind === 'tryout'}
                  itemId={item.id}
                  minWords={item.discussion.minWords}
                  completed={item.completed}
                  onPosted={reload}
                />
              )}
              {(item.kind === 'assignment' || item.kind === 'reflection') && (
                <Assignment
                  itemId={item.id}
                  submissions={item.submissions ?? []}
                  onSubmitted={reload}
                  reflection={item.kind === 'reflection' ? (item.reflection ?? { minWords: 0 }) : undefined}
                  rubric={item.rubric ?? []}
                />
              )}

              {item.attendanceRequired && !item.completed && (
                <p className="mt-8 rounded-xl bg-night-50 p-4 text-sm text-muted">
                  Träffen har närvarokrav. Läraren registrerar närvaron efter träffen, och då blir delen klar.
                </p>
              )}
              {item.selfCompletable && !item.completed && item.kind !== 'page' && (
                <div className="mt-8 flex flex-col gap-3 rounded-xl bg-gradient-to-r from-night-50 to-aurora-100/50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted">
                    {item.kind === 'video'
                      ? 'Delen blir klar automatiskt när du sett större delen av filmen – eller markera den själv.'
                      : 'Markera delen som klar när du är färdig.'}
                  </p>
                  <Button className="shrink-0" onClick={markDone}>
                    ✓ Markera som klar
                  </Button>
                </div>
              )}
            </Card>

            <JournalNote key={item.id} itemId={item.id} />

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
              {data.prev ? (
                <Link
                  to={`/kurs/del/${data.prev.id}`}
                  className="card-lift rounded-xl bg-white p-4 shadow-sm ring-1 ring-night-800/5"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                    ← Föregående
                  </span>
                  <span className="mt-1 block truncate font-semibold text-night-800">{data.prev.title}</span>
                </Link>
              ) : (
                <span className="hidden sm:block" />
              )}
              {data.next ? (
                <Link
                  to={`/kurs/del/${data.next.id}`}
                  className={cx(
                    'card-lift rounded-xl p-4 text-right shadow-md',
                    item.completed || !item.required
                      ? 'bg-gradient-to-r from-night-800 to-aurora-600 text-white'
                      : 'bg-white text-night-800 ring-1 ring-night-800/10',
                  )}
                >
                  <span
                    className={cx(
                      'text-xs font-semibold uppercase tracking-wide',
                      item.completed || !item.required ? 'text-white/70' : 'text-muted',
                    )}
                  >
                    {data.next.moduleChange ? 'Nästa modul →' : 'Nästa →'}
                  </span>
                  <span className="mt-1 block truncate font-bold">{data.next.title}</span>
                </Link>
              ) : (
                <Link
                  to={data.progress.allDone ? '/intyg' : '/kurs'}
                  className="card-lift rounded-xl bg-gradient-to-r from-night-800 to-aurora-600 p-4 text-right text-white shadow-md"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-white/70">
                    Sista delen
                  </span>
                  <span className="mt-1 block font-bold">
                    {data.progress.allDone ? '★ Hämta ditt intyg' : 'Till kursöversikten'}
                  </span>
                </Link>
              )}
            </div>
          </article>

          <aside className="space-y-4 lg:sticky lg:top-14 lg:self-start">
            <Card className="!p-0 overflow-hidden">
              <div className="flex items-center gap-3 bg-gradient-to-r from-night-900 to-night-800 px-5 py-4 text-white">
                <MiniRingDark percent={modulePercent} />
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wide text-white/70">Modul {data.module.number}</p>
                  <p className="truncate font-bold">{data.module.title}</p>
                  <p className="text-xs text-white/70">
                    {moduleDoneCount} av {moduleRequired.length} klara
                  </p>
                </div>
              </div>
              <ul className="py-2">
                {data.module.items.map((i) => (
                  <li key={i.id}>
                    <Link
                      to={`/kurs/del/${i.id}`}
                      aria-current={i.id === item.id ? 'page' : undefined}
                      className={cx(
                        'flex items-center gap-2 border-l-4 px-4 py-2 text-sm hover:bg-night-50',
                        i.id === item.id
                          ? 'border-aurora-400 bg-night-50 font-semibold'
                          : 'border-transparent',
                      )}
                    >
                      <KindIcon kind={i.kind} className="h-4 w-4 text-night-700" />
                      <span className={cx('flex-1', i.completed && i.id !== item.id && 'text-muted')}>
                        {i.title}
                      </span>
                      {i.completed ? (
                        <CheckIcon className="h-4 w-4 text-aurora-500" />
                      ) : (
                        <span
                          className="h-4 w-4 rounded-full border-2 border-night-800/20"
                          aria-hidden="true"
                        />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </aside>
        </div>
      </Container>

      <Confetti fire={confetti} />
      <Toast message={toast} onDone={() => setToast(null)} />
      {celebration && (
        <CelebrationDialog
          celebration={celebration}
          nextId={data.next?.id ?? null}
          onClose={() => setCelebration(null)}
        />
      )}
    </>
  );
}

const PRAISE = [
  'Klart! Bra jobbat.',
  'Snyggt – en del till avklarad!',
  'Klart! Du är på god väg.',
  'Bra! Fortsätt så.',
];

function MiniRingDark({ percent }: { percent: number }) {
  const size = 44;
  const r = 18;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" aria-hidden="true" className="shrink-0">
      <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
      <circle
        className="ring-arc"
        cx="22"
        cy="22"
        r={r}
        fill="none"
        stroke="#3fb68b"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - percent / 100)}
        transform="rotate(-90 22 22)"
      />
      <text x="22" y="26" textAnchor="middle" fontSize="11" fontWeight="700" fill="#fff">
        {percent}%
      </text>
    </svg>
  );
}

function CelebrationDialog({
  celebration,
  nextId,
  onClose,
}: {
  celebration: Celebration;
  nextId: number | null;
  onClose: () => void;
}) {
  const course = celebration.kind === 'course';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-night-950/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="firande"
        className="pop-in aurora-sky relative w-full max-w-md overflow-hidden rounded-3xl p-8 text-center text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="stars" />
        <div className="flex justify-center">
          <Medal
            earned
            size={104}
            kind={course ? 'certificate' : 'module'}
            number={celebration.moduleNumber}
            label=""
          />
        </div>
        <h2 id="firande" className="mt-2 text-2xl font-extrabold">
          {course ? 'Du har gjort hela kursen!' : 'Modulmärke taget!'}
        </h2>
        <p className="mt-2 text-white/85">
          {course
            ? 'Fantastiskt jobbat. Ditt intyg är klart att hämta – och ett mejl är på väg.'
            : `Du har klarat alla delar i ”${celebration.moduleTitle}”.`}
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          {course ? (
            <LinkButton to="/intyg" className="bg-gradient-to-r from-amber-400 to-yellow-300 !text-night-950">
              ★ Hämta intyget
            </LinkButton>
          ) : nextId ? (
            <LinkButton to={`/kurs/del/${nextId}`} className="bg-white !text-night-900 hover:!bg-night-100">
              Vidare till nästa modul →
            </LinkButton>
          ) : null}
          <Button variant="ghost" className="!text-white hover:!bg-white/10" onClick={onClose}>
            Stäng
          </Button>
        </div>
      </div>
    </div>
  );
}

function MeetingInfo({
  meeting,
  calendarUrl,
}: {
  meeting: NonNullable<ItemResponse['item']['meeting']>;
  calendarUrl: string | null;
}) {
  if (!meeting.startsAt && !meeting.location && !meeting.url) return null;
  return (
    <div className="mb-6 rounded-xl bg-night-50 p-4 text-sm">
      {meeting.startsAt && (
        <p>
          <strong>Tid:</strong> {formatDate(meeting.startsAt, true)}
        </p>
      )}
      {meeting.location && (
        <p>
          <strong>Plats:</strong> {meeting.location}
        </p>
      )}
      {meeting.url && (
        <p className="mt-2">
          <a
            href={meeting.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-night-700 underline"
          >
            Öppna länken
          </a>
        </p>
      )}
      {meeting.startsAt && calendarUrl && (
        <p className="mt-2">
          <a href={calendarUrl} download className="font-semibold text-night-700 underline">
            📅 Lägg till i kalendern
          </a>
        </p>
      )}
    </div>
  );
}

// ---------- Quiz ----------

interface QuizResult {
  score: number;
  maxScore: number;
  percent: number;
  passed: boolean;
  assessment: 'pre' | 'post' | null;
  questions: { questionId: number; correct: boolean; feedback: string[] }[];
}

function Quiz({
  itemId,
  quiz,
  onChanged,
}: {
  itemId: number;
  quiz: NonNullable<ItemResponse['item']['quiz']>;
  onChanged: () => Promise<void>;
}) {
  // Ordningsfrågor har alltid ett svar: ordningen som de visas i.
  const initialAnswers = () =>
    Object.fromEntries(
      quiz.questions.filter((q) => q.kind === 'ordering').map((q) => [q.id, q.choices.map((c) => c.id)]),
    );
  const [answers, setAnswers] = useState<Record<string, string[]>>(initialAnswers);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [gradedQuiz, setGradedQuiz] = useState<typeof quiz | null>(null);
  const submit = useAction(async () => {
    try {
      const result = await api.post<QuizResult>(`/api/course/items/${itemId}/quiz`, {
        answers: { ...initialAnswers(), ...answers },
        attemptToken: quiz.attemptToken,
      });
      setGradedQuiz(quiz);
      setResult(result);
      // Ett nytt försök har nya presentationstoken, även utan frågebank.
      await onChanged();
      return result;
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await onChanged();
        setAnswers({});
        setResult(null);
        setGradedQuiz(null);
      }
      throw error;
    }
  });
  useEffect(() => {
    // Vid frågebank visas det rättade underlaget tills deltagaren väljer ett nytt försök.
    if (!(quiz.bankSize && result && !result.passed)) setAnswers(initialAnswers());
  }, [quiz.attemptToken]);
  const reviewingBank = !!quiz.bankSize && !!result && !result.passed;
  const shownQuiz = reviewingBank && gradedQuiz ? gradedQuiz : quiz;
  const best = quiz.attempts.reduce(
    (m, a) => Math.max(m, a.max_score ? Math.round((a.score / a.max_score) * 100) : 0),
    0,
  );

  const toggle = (qid: number, cid: string, multiple: boolean) => {
    setResult(null);
    setAnswers((prev) => {
      const current = prev[qid] ?? [];
      const next = multiple
        ? current.includes(cid)
          ? current.filter((c) => c !== cid)
          : [...current, cid]
        : [cid];
      return { ...prev, [qid]: next };
    });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await submit.run();
    if (res) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!quiz.questions.length) return <Alert kind="info">Quizet har inga frågor än.</Alert>;
  const pre = quiz.assessment === 'pre';
  const preDone = pre && quiz.attempts.length > 0 && !result;
  if (preDone) {
    return (
      <Alert kind="success">
        Du har gjort kunskapskontrollen och fick {best} %. Den visar var du började – i slutet av kursen gör
        du en likadan och ser hur mycket du lärt dig.
      </Alert>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-8 space-y-6">
      <div className="rounded-xl bg-night-50 p-4 text-sm">
        {pre ? (
          'En kort kunskapskontroll innan du börjar. Det gör inget om du inte kan svaren – den visar var du står nu, och du gör den bara en gång.'
        ) : (
          <>
            {quiz.assessment === 'post' &&
              'Kunskapskontrollen i slutet av kursen. Jämför med hur det gick i början! '}
            Du behöver {quiz.passPercent} % rätt för att bli godkänd. Du kan göra quizet hur många gånger du
            vill.
            {quiz.attempts.length > 0 && <> Ditt bästa resultat hittills: {best} %.</>}
            {quiz.bankSize && (
              <> Frågorna dras ur en bank med {quiz.bankSize} frågor, så ett nytt försök ger nya frågor.</>
            )}
          </>
        )}
      </div>
      {result && (
        <Alert kind={result.passed || pre ? 'success' : 'info'}>
          {pre
            ? `Tack! Du fick ${result.score} av ${result.maxScore} rätt (${result.percent} %). Nu vet du var du började.`
            : result.passed
              ? `Godkänt! Du fick ${result.score} av ${result.maxScore} rätt.`
              : `Du fick ${result.score} av ${result.maxScore} rätt (${result.percent} %). Läs återkopplingen och försök igen.`}
        </Alert>
      )}
      {shownQuiz.questions.map((q, index) => {
        const r = result?.questions.find((x) => x.questionId === q.id);
        const multiple = q.kind === 'multiple';
        return (
          <fieldset
            key={q.id}
            disabled={submit.pending || reviewingBank}
            className={cx(
              'rounded-xl p-4 ring-1',
              r
                ? r.correct
                  ? 'ring-aurora-400 bg-aurora-100/40'
                  : 'ring-amber-300 bg-amber-50'
                : 'ring-night-800/10',
            )}
          >
            <legend className="px-1 text-sm font-semibold text-muted">
              Fråga {index + 1} av {shownQuiz.questions.length}
              {multiple && ' · flera svar kan vara rätt'}
              {q.kind === 'ordering' && ' · sätt i rätt ordning'}
              {q.kind === 'matching' && ' · para ihop'}
              {q.kind === 'short' && ' · skriv ett kort svar'}
            </legend>
            <Segments segments={q.text} />
            {q.kind === 'short' ? (
              <input
                aria-label={`Svar på fråga ${index + 1}`}
                className="mt-3 w-full rounded-lg border border-night-800/20 px-3 py-2"
                value={answers[q.id]?.[0] ?? ''}
                onChange={(e) => {
                  setResult(null);
                  setAnswers((prev) => ({ ...prev, [q.id]: [e.target.value] }));
                }}
              />
            ) : q.kind === 'ordering' ? (
              <OrderingAnswer
                choices={q.choices}
                order={answers[q.id] ?? q.choices.map((c) => c.id)}
                onChange={(order) => {
                  setResult(null);
                  setAnswers((prev) => ({ ...prev, [q.id]: order }));
                }}
              />
            ) : q.kind === 'matching' ? (
              <div className="mt-3 space-y-2">
                {q.choices.map((left) => {
                  const current =
                    (answers[q.id] ?? []).find((a) => a.startsWith(`${left.id}=`))?.split('=')[1] ?? '';
                  return (
                    <label key={left.id} className="grid gap-2 sm:grid-cols-[1fr_1fr] sm:items-center">
                      <span>{left.text}</span>
                      <select
                        className="rounded-lg border border-night-800/20 bg-white px-3 py-2"
                        value={current}
                        onChange={(e) => {
                          setResult(null);
                          setAnswers((prev) => ({
                            ...prev,
                            [q.id]: [
                              ...(prev[q.id] ?? []).filter((a) => !a.startsWith(`${left.id}=`)),
                              ...(e.target.value ? [`${left.id}=${e.target.value}`] : []),
                            ],
                          }));
                        }}
                      >
                        <option value="">Välj …</option>
                        {q.matches?.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.text}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {q.choices.map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-start gap-3 rounded-lg p-2 hover:bg-white"
                  >
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      name={`q${q.id}`}
                      className="mt-1 h-4 w-4 accent-night-800"
                      checked={(answers[q.id] ?? []).includes(c.id)}
                      onChange={() => toggle(q.id, c.id, multiple)}
                    />
                    <span>{c.text}</span>
                  </label>
                ))}
              </div>
            )}
            {r && (
              <div className="mt-3 text-sm">
                <p className="font-semibold">{r.correct ? 'Rätt!' : 'Inte riktigt.'}</p>
                {r.feedback.map((f, i) => (
                  <p key={i} className="mt-1">
                    {f}
                  </p>
                ))}
              </div>
            )}
          </fieldset>
        );
      })}
      <ErrorMessage error={submit.error} />
      {quiz.bankSize && result && !result.passed && !pre ? (
        <Button
          type="button"
          onClick={() => {
            setResult(null);
            setGradedQuiz(null);
            setAnswers(initialAnswers());
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          Nytt försök med nya frågor
        </Button>
      ) : (
        !(pre && result) && (
          <Button type="submit" pending={submit.pending}>
            {result && !result.passed ? 'Rätta igen' : 'Rätta mina svar'}
          </Button>
        )
      )}
    </form>
  );
}

function OrderingAnswer({
  choices,
  order,
  onChange,
}: {
  choices: { id: string; text: string }[];
  order: string[];
  onChange: (order: string[]) => void;
}) {
  const text = new Map(choices.map((c) => [c.id, c.text]));
  const move = (i: number, d: -1 | 1) => {
    const next = [...order];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <ol className="mt-3 space-y-2">
      {order.map((id, i) => (
        <li key={id} className="flex items-center gap-2 rounded-lg bg-white p-2 ring-1 ring-night-800/10">
          <span className="w-6 text-center font-bold text-muted">{i + 1}</span>
          <span className="flex-1">{text.get(id)}</span>
          <button
            type="button"
            className="rounded px-2 py-1 hover:bg-night-100 disabled:opacity-30"
            aria-label={`Flytta upp: ${text.get(id)}`}
            disabled={i === 0}
            onClick={() => move(i, -1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="rounded px-2 py-1 hover:bg-night-100 disabled:opacity-30"
            aria-label={`Flytta ned: ${text.get(id)}`}
            disabled={i === order.length - 1}
            onClick={() => move(i, 1)}
          >
            ↓
          </button>
        </li>
      ))}
    </ol>
  );
}

// ---------- Min AI-dagbok ----------

function JournalNote({ itemId }: { itemId: number }) {
  const { data } = useApi<{ body: string; updatedAt: string | null }>(`/api/course/items/${itemId}/note`);
  const [body, setBody] = useState<string | null>(null);
  const { status, recovered, retry } = useAutosave(`/api/course/items/${itemId}/note`, body, 800, (b) => ({
    body: b,
  }));
  const value = body ?? recovered ?? data?.body ?? '';

  if (!data) return null;
  return (
    <details
      className="mt-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:px-7"
      open={!!data.body || undefined}
    >
      <summary className="cursor-pointer font-bold text-night-900">
        📓 Min dagbok <span className="font-normal text-muted">– tankar och idéer till den här delen</span>
      </summary>
      <div className="mt-3">
        <TextArea
          label="Anteckningar"
          rows={4}
          value={value}
          onChange={(e) => setBody(e.target.value)}
          hint={
            status === 'saving'
              ? 'Sparar …'
              : status === 'saved'
                ? 'Sparat. Bara du ser dina anteckningar.'
                : status === 'error'
                  ? 'Kunde inte spara. Försök igen.'
                  : status === 'conflict'
                    ? 'Anteckningen sparades inte eftersom en senare ändring redan finns. Din text finns kvar här.'
                    : 'Sparas automatiskt. Bara du ser dina anteckningar – de samlas i Min AI-dagbok.'
          }
        />
        {(status === 'error' || status === 'conflict') && (
          <Button type="button" variant="secondary" onClick={retry}>
            {status === 'conflict' ? 'Spara min text igen' : 'Försök spara igen'}
          </Button>
        )}
        <Link to="/dagbok" className="mt-2 inline-block text-sm font-semibold text-night-700 underline">
          Öppna hela dagboken
        </Link>
      </div>
    </details>
  );
}

// ---------- Diskussion ----------

interface Post {
  id: number;
  body: string;
  createdAt: string;
  author: string;
  municipality: string;
  teacher: boolean;
  mine: boolean;
  /** Bara för lärare: dolda inlägg visas markerade. */
  hidden?: boolean;
  hiddenReason?: string | null;
  feedback?: { stars: [string, string]; wish: string } | null;
}
interface PostsResponse {
  page: number;
  pages: number;
  total: number;
  myPostCount: number;
  posts: (Post & { replies: Post[] })[];
  /** Prova i klassrummet: kamratåterkoppling (två stjärnor och en önskan). */
  peerFeedback?: {
    target: number;
    given: number;
    suggestions: { id: number; author: string; feedbackCount: number }[];
  };
}

const wordCount = (s: string) => (s.trim() ? s.trim().split(/\s+/u).length : 0);

function Discussion({
  tryout,
  itemId,
  minWords,
  completed,
  onPosted,
}: {
  tryout?: boolean;
  itemId: number;
  minWords: number;
  completed: boolean;
  onPosted: () => void;
}) {
  const [page, setPage] = useState(1);
  const { data, reload } = useApi<PostsResponse>(`/api/course/items/${itemId}/posts?page=${page}`);
  const [body, setBody] = useState('');
  const post = useAction(() => api.post(`/api/course/items/${itemId}/posts`, { body }));
  const words = wordCount(body);

  return (
    <section className="mt-8 border-t border-night-100 pt-6" aria-labelledby="diskussion">
      <h2 id="diskussion" className="text-xl font-bold text-night-900">
        {tryout ? 'Rapporter från klassrummet' : 'Diskussion'}
      </h2>
      {!completed && (
        <p className="mt-1 text-sm text-muted">
          {tryout
            ? 'Pröva aktiviteten med dina elever och berätta kort hur det gick: vad gjorde ni, vad fungerade och vad skulle du ändra? Delen blir klar när du skrivit din rapport'
            : 'Delen blir klar när du skrivit ett eget inlägg'}
          {minWords ? ` på minst ${minWords} ord` : ''}.
        </p>
      )}
      <form
        className="mt-4 space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await post.run()) {
            setBody('');
            setPage(1);
            await reload();
            onPosted();
          }
        }}
      >
        <TextArea
          label={
            tryout
              ? completed
                ? 'Skriv en ny rapport'
                : 'Din rapport'
              : completed
                ? 'Skriv ett nytt inlägg'
                : 'Ditt inlägg'
          }
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          hint={`${words} ord${minWords ? ` (minst ${minWords})` : ''}. Inlägget syns för alla i kursomgången.`}
          error={post.error?.fields.body}
        />
        {post.error && !post.error.fields.body && <ErrorMessage error={post.error} />}
        <Button type="submit" pending={post.pending} disabled={!body.trim()}>
          Publicera
        </Button>
      </form>

      {tryout && data?.peerFeedback && data.peerFeedback.target > 0 && (
        <PeerFeedbackPrompt status={data.peerFeedback} />
      )}

      <div className="mt-8 space-y-4">
        {data?.total === 0 && (
          <p className="text-muted">
            {tryout
              ? 'Inga rapporter än. Bli den första att dela hur det gick!'
              : 'Inga inlägg än. Bli den första!'}
          </p>
        )}
        {data?.posts.map((p) => (
          <PostCard key={p.id} itemId={itemId} post={p} onReply={reload} tryout={tryout} />
        ))}
      </div>
      {data && data.pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Nyare
          </Button>
          <span className="text-sm text-muted">
            Sida {data.page} av {data.pages}
          </span>
          <Button variant="secondary" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>
            Äldre
          </Button>
        </div>
      )}
    </section>
  );
}

/** Påminner om att ge återkoppling på kollegors rapporter och föreslår vilka. */
function PeerFeedbackPrompt({ status }: { status: NonNullable<PostsResponse['peerFeedback']> }) {
  const done = status.given >= status.target;
  return (
    <div className="mt-6 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
      <p className="font-semibold text-night-900">
        <span aria-hidden="true">⭐⭐💡 </span>Två stjärnor och en önskan
      </p>
      <p className="mt-1 text-sm">
        {done
          ? `Tack! Du har gett återkoppling till ${status.given} ${status.given === 1 ? 'kollega' : 'kollegor'}.`
          : `Läs några kollegors rapporter och ge återkoppling: två saker som var bra och en sak de kan pröva nästa gång. Du har gett ${status.given} av ${status.target}.`}
      </p>
      {!done && status.suggestions.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2 text-sm">
          {status.suggestions.map((s) => (
            <li key={s.id}>
              <a
                href={`#inlagg-${s.id}`}
                className="inline-block rounded-full bg-white px-3 py-1 font-semibold text-night-800 ring-1 ring-night-800/10 hover:bg-night-50"
              >
                {s.author}
                <span className="ml-1 font-normal text-muted">
                  ({s.feedbackCount ? `${s.feedbackCount} har svarat` : 'ingen återkoppling än'})
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PeerFeedbackForm({
  itemId,
  parentId,
  author,
  onDone,
  onCancel,
}: {
  itemId: number;
  parentId: number;
  author: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({ star1: '', star2: '', wish: '' });
  const send = useAction(() =>
    api.post(`/api/course/items/${itemId}/posts`, {
      parentId,
      feedback: { stars: [form.star1, form.star2], wish: form.wish },
    }),
  );
  const ready =
    form.star1.trim().length >= 3 && form.star2.trim().length >= 3 && form.wish.trim().length >= 3;
  return (
    <form
      className="mt-3 space-y-3 rounded-xl bg-white p-4 ring-1 ring-night-800/10"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await send.run()) onDone();
      }}
    >
      <p className="text-sm font-semibold">Återkoppling till {author}</p>
      <TextArea
        label="⭐ Något som var bra"
        rows={2}
        value={form.star1}
        onChange={(e) => setForm({ ...form, star1: e.target.value })}
      />
      <TextArea
        label="⭐ Något mer som var bra"
        rows={2}
        value={form.star2}
        onChange={(e) => setForm({ ...form, star2: e.target.value })}
      />
      <TextArea
        label="💡 En önskan: något att pröva nästa gång"
        rows={2}
        value={form.wish}
        onChange={(e) => setForm({ ...form, wish: e.target.value })}
      />
      <ErrorMessage error={send.error} />
      <div className="flex gap-2">
        <Button type="submit" pending={send.pending} disabled={!ready}>
          Skicka återkoppling
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Avbryt
        </Button>
      </div>
    </form>
  );
}

function PostCard({
  itemId,
  post,
  onReply,
  tryout,
}: {
  itemId: number;
  post: Post & { replies: Post[] };
  onReply: () => void;
  tryout?: boolean;
}) {
  const [replying, setReplying] = useState(false);
  const [giving, setGiving] = useState(false);
  const [body, setBody] = useState('');
  const reply = useAction(() => api.post(`/api/course/items/${itemId}/posts`, { body, parentId: post.id }));
  const canGiveFeedback =
    tryout && !post.mine && !post.teacher && !post.hidden && !post.replies.some((r) => r.mine && r.feedback);
  return (
    <div id={`inlagg-${post.id}`} className="scroll-mt-24 rounded-xl bg-night-50 p-4">
      <PostBody post={post} onChange={onReply} />
      {post.replies.length > 0 && (
        <div className="mt-3 space-y-3 border-l-2 border-night-800/10 pl-4">
          {post.replies.map((r) => (
            <PostBody key={r.id} post={r} onChange={onReply} />
          ))}
        </div>
      )}
      {giving ? (
        <PeerFeedbackForm
          itemId={itemId}
          parentId={post.id}
          author={post.author}
          onDone={() => {
            setGiving(false);
            onReply();
          }}
          onCancel={() => setGiving(false)}
        />
      ) : replying ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await reply.run()) {
              setBody('');
              setReplying(false);
              onReply();
            }
          }}
        >
          <TextArea label="Ditt svar" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
          <ErrorMessage error={reply.error} />
          <div className="flex gap-2">
            <Button type="submit" pending={reply.pending} disabled={!body.trim()}>
              Svara
            </Button>
            <Button type="button" variant="ghost" onClick={() => setReplying(false)}>
              Avbryt
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-2 flex flex-wrap gap-4">
          {canGiveFeedback && (
            <button
              type="button"
              className="text-sm font-semibold text-night-700 underline"
              onClick={() => setGiving(true)}
            >
              Ge återkoppling (två stjärnor och en önskan)
            </button>
          )}
          <button
            type="button"
            className="text-sm font-semibold text-night-700 underline"
            onClick={() => setReplying(true)}
          >
            Svara
          </button>
        </div>
      )}
    </div>
  );
}

function PostBody({ post, onChange }: { post: Post; onChange: () => void }) {
  const { user } = useAuth();
  const moderator = isTeacher(user);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const [reason, setReason] = useState('');
  const remove = useAction(() => api.delete(`/api/course/posts/${post.id}`));
  const report = useAction(() => api.post(`/api/course/posts/${post.id}/report`, { reason }));
  const hide = useAction((hidden: boolean) => api.post(`/api/teacher/posts/${post.id}/hide`, { hidden }));
  const action = 'text-xs font-semibold text-muted underline hover:text-night-800';

  return (
    <div className={cx(post.hidden && 'opacity-60')}>
      <p className="text-sm">
        <span className="font-semibold">{post.author}</span>
        {post.teacher && (
          <span className="ml-2">
            <Badge tone="green">Lärare</Badge>
          </span>
        )}
        {post.hidden && (
          <span className="ml-2">
            <Badge tone="red">
              {post.hiddenReason === 'author' ? 'Borttaget av skribenten' : 'Dolt för deltagare'}
            </Badge>
          </span>
        )}
        <span className="text-muted">
          {post.municipality && !post.teacher ? ` · ${post.municipality}` : ''} ·{' '}
          {formatDate(post.createdAt, true)}
        </span>
      </p>
      {post.feedback ? (
        <dl className="mt-1 space-y-1 text-sm">
          {post.feedback.stars.map((s, i) => (
            <div key={i} className="flex gap-2">
              <dt aria-label="Stjärna">⭐</dt>
              <dd className="whitespace-pre-line">{s}</dd>
            </div>
          ))}
          <div className="flex gap-2">
            <dt aria-label="Önskan">💡</dt>
            <dd className="whitespace-pre-line">{post.feedback.wish}</dd>
          </div>
        </dl>
      ) : (
        <p className="mt-1 whitespace-pre-line">{post.body}</p>
      )}
      <div className="mt-1 flex flex-wrap gap-3">
        {post.mine && !post.hidden && (
          <button
            type="button"
            className={action}
            onClick={async () => {
              if (!window.confirm('Ta bort ditt inlägg? Det går inte att ångra.')) return;
              if ((await remove.run()) !== undefined) onChange();
            }}
          >
            Ta bort
          </button>
        )}
        {!post.mine && !moderator && !reported && (
          <button type="button" className={action} onClick={() => setReporting(!reporting)}>
            Anmäl
          </button>
        )}
        {moderator && post.hiddenReason !== 'author' && (
          <button
            type="button"
            className={action}
            onClick={async () => {
              if ((await hide.run(!post.hidden)) !== undefined) onChange();
            }}
          >
            {post.hidden ? 'Visa igen' : 'Dölj'}
          </button>
        )}
      </div>
      {reported && <p className="mt-1 text-xs text-muted">Tack! Kursens lärare har fått din anmälan.</p>}
      {reporting && (
        <form
          className="mt-2 space-y-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if ((await report.run()) !== undefined) {
              setReporting(false);
              setReported(true);
            }
          }}
        >
          <TextArea
            label="Varför är inlägget olämpligt?"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={report.error?.fields.reason}
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="secondary"
              pending={report.pending}
              disabled={reason.trim().length < 3}
            >
              Skicka anmälan
            </Button>
            <Button type="button" variant="ghost" onClick={() => setReporting(false)}>
              Avbryt
            </Button>
          </div>
        </form>
      )}
      <ErrorMessage
        error={remove.error ?? hide.error ?? (report.error?.fields.reason ? null : report.error)}
      />
    </div>
  );
}

// ---------- Uppgift ----------

const statusText: Record<Submission['status'], { label: string; tone: 'blue' | 'green' | 'amber' }> = {
  submitted: { label: 'Inlämnad – väntar på rättning', tone: 'blue' },
  approved: { label: 'Godkänd', tone: 'green' },
  revise: { label: 'Komplettering behövs', tone: 'amber' },
};

/** Utkast som sparas på servern medan deltagaren skriver, så att inget försvinner. */
function useDraft(itemId: number, baseSubmissionId: number | null) {
  const { data, loading } = useApi<{ text: string; baseSubmissionId: number | null }>(
    `/api/course/items/${itemId}/draft?submission=${baseSubmissionId ?? ''}`,
  );
  const [edited, setEdited] = useState<string | null>(null);
  const [cleared, setCleared] = useState(false);
  const { status, recovered, retry, commit } = useAutosave(
    `/api/course/items/${itemId}/draft`,
    edited,
    1000,
    (t) => ({
      text: t,
      baseSubmissionId: data?.baseSubmissionId ?? baseSubmissionId,
    }),
  );
  const text = cleared ? '' : (edited ?? recovered ?? data?.text ?? '');
  return {
    text,
    setText: (v: string) => {
      setCleared(false);
      setEdited(v);
    },
    clear: () => {
      setEdited(null);
      setCleared(true);
    },
    saved: status === 'saved' && !!text.trim(),
    status,
    retry,
    commit,
    ready: !loading && !!data,
  };
}

function Assignment({
  itemId,
  submissions,
  onSubmitted,
  reflection,
  rubric = [],
}: {
  itemId: number;
  submissions: Submission[];
  onSubmitted: () => Promise<void>;
  /** Reflektion: bara text, minsta antal ord, klar direkt. */
  reflection?: { minWords: number };
  rubric?: { id: string; text: string }[];
}) {
  const latest = submissions[0];
  const {
    text,
    setText,
    clear,
    saved,
    status: draftStatus,
    retry,
    commit,
    ready,
  } = useDraft(itemId, latest?.id ?? null);
  const [file, setFile] = useState<File | null>(null);
  const [formKey, setFormKey] = useState(0);
  const submit = useAction(async () => {
    const form = new FormData();
    form.append('text', text);
    if (file) form.append('file', file);
    const result = await commit(() => api.post(`/api/course/items/${itemId}/submissions`, form));
    clear();
    setFile(null);
    setFormKey((current) => current + 1);
    await onSubmitted();
    return result;
  });
  const canSubmit = latest?.status !== 'approved';
  const words = wordCount(text);
  const draftError =
    draftStatus === 'error' || draftStatus === 'conflict' ? (
      <div className="space-y-2">
        <Alert>
          {draftStatus === 'conflict'
            ? 'Utkastet sparades inte eftersom en senare ändring eller inlämning finns. Kontrollera texten innan du sparar den som ett nytt utkast.'
            : 'Utkastet kunde inte sparas på servern. Försök igen innan du stänger sidan.'}
        </Alert>
        <Button type="button" variant="secondary" onClick={retry} disabled={!ready}>
          {draftStatus === 'conflict' ? 'Spara som nytt utkast' : 'Försök spara utkastet igen'}
        </Button>
      </div>
    ) : null;

  if (reflection) {
    return (
      <section className="mt-8 border-t border-night-100 pt-6" aria-labelledby="reflektion">
        <h2 id="reflektion" className="text-xl font-bold text-night-900">
          Din reflektion
        </h2>
        {latest && (
          <div className="mt-3 space-y-2 rounded-xl bg-night-50 p-4">
            <p className="text-sm text-muted">Inlämnad {formatDate(latest.submitted_at, true)}</p>
            <p className="whitespace-pre-line">{latest.text}</p>
            {latest.feedback && (
              <div className="rounded-lg bg-white p-3">
                <p className="text-sm font-semibold">Återkoppling från läraren</p>
                <p className="mt-1 whitespace-pre-line">{latest.feedback}</p>
              </div>
            )}
          </div>
        )}
        {canSubmit && (
          <form
            className="mt-5 space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              await submit.run();
            }}
          >
            {latest?.status === 'revise' && (
              <Alert kind="info">Läraren har bett dig utveckla din reflektion. Skriv en ny version.</Alert>
            )}
            <TextArea
              label="Dina tankar"
              disabled={submit.pending || !ready}
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              hint={`${words} ord${reflection.minWords ? ` (minst ${reflection.minWords})` : ''}.${saved ? ' Utkastet är sparat.' : ''} Delen blir klar när du lämnat in. Läraren läser ett urval av reflektionerna.`}
              error={submit.error?.fields.text}
            />
            {draftError}
            {submit.error && !submit.error.fields.text && <ErrorMessage error={submit.error} />}
            <Button
              type="submit"
              pending={submit.pending}
              disabled={!ready || words < Math.max(1, reflection.minWords)}
            >
              Lämna in
            </Button>
          </form>
        )}
      </section>
    );
  }

  return (
    <section className="mt-8 border-t border-night-100 pt-6" aria-labelledby="inlamning">
      <h2 id="inlamning" className="text-xl font-bold text-night-900">
        Din inlämning
      </h2>
      {latest && (
        <div className="mt-3 space-y-2 rounded-xl bg-night-50 p-4">
          <p className="flex flex-wrap items-center gap-2">
            <Badge tone={statusText[latest.status].tone}>{statusText[latest.status].label}</Badge>
            <span className="text-sm text-muted">
              Försök {latest.attempt}, inlämnat {formatDate(latest.submitted_at, true)}
            </span>
          </p>
          {latest.file_name && (
            <p className="text-sm">
              Fil:{' '}
              <a className="text-night-700 underline" href={`/api/submissions/${latest.id}/file`}>
                {latest.file_name}
              </a>
            </p>
          )}
          {latest.feedback && (
            <div className="rounded-lg bg-white p-3">
              <p className="text-sm font-semibold">Återkoppling från läraren</p>
              <p className="mt-1 whitespace-pre-line">{latest.feedback}</p>
            </div>
          )}
        </div>
      )}
      {rubric.length > 0 && (
        <div className="mt-4 rounded-xl p-4 ring-1 ring-night-800/10">
          <p className="text-sm font-semibold">Det här bedöms</p>
          <ul className="mt-2 space-y-1 text-sm">
            {rubric.map((c) => {
              const result = latest?.rubric?.[c.id];
              return (
                <li key={c.id} className="flex items-start gap-2">
                  <span
                    aria-label={
                      result === true ? 'Uppfyllt' : result === false ? 'Inte uppfyllt än' : undefined
                    }
                    className={
                      result === true ? 'text-aurora-600' : result === false ? 'text-amber-700' : 'text-muted'
                    }
                  >
                    {result === true ? '✓' : result === false ? '○' : '•'}
                  </span>
                  <span>{c.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {canSubmit && (
        <form
          key={formKey}
          className="mt-5 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            await submit.run();
          }}
        >
          {latest?.status === 'submitted' && (
            <Alert kind="info">Du kan lämna in en ny version. Läraren rättar den senaste.</Alert>
          )}
          <TextArea
            label="Text (valfritt om du bifogar en fil)"
            disabled={submit.pending || !ready}
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            hint={saved ? 'Utkastet sparas automatiskt medan du skriver.' : undefined}
          />
          {draftError}
          <div>
            <label className="block text-sm font-semibold" htmlFor="inlamning-fil">
              Fil (PDF, Word, OpenDocument eller text)
            </label>
            <input
              id="inlamning-fil"
              type="file"
              disabled={submit.pending}
              accept=".pdf,.doc,.docx,.odt,.txt,.rtf,.pptx,.png,.jpg,.jpeg"
              className="mt-1 block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-night-100 file:px-4 file:py-2 file:font-semibold"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <ErrorMessage error={submit.error} />
          <Button type="submit" pending={submit.pending} disabled={!ready || (!text.trim() && !file)}>
            {latest ? 'Lämna in ny version' : 'Lämna in'}
          </Button>
        </form>
      )}
      {submissions.length > 1 && (
        <details className="mt-6">
          <summary className="cursor-pointer text-sm font-semibold text-night-700">Tidigare försök</summary>
          <ul className="mt-2 space-y-2 text-sm">
            {submissions.slice(1).map((s) => (
              <li key={s.id} className="rounded-lg bg-night-50 p-3">
                Försök {s.attempt} · {formatDate(s.submitted_at)} · {statusText[s.status].label}
                {s.feedback && <p className="mt-1 whitespace-pre-line text-muted">{s.feedback}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
